import { useReducer, useEffect, useRef, useMemo } from "react";
import type { Player, GameState, GameResult } from "../types";
import {
  gameReducer,
  createInitialState,
  resolveRound,
} from "../utils/gameLogic";
import { GAME_CONFIG } from "../config/gameConfig";

/**
 * Core game state hook for Eggs Fighting Game.
 *
 * Manages the complete game lifecycle using useReducer for state and
 * useRef for all timers. Phase transitions are driven automatically
 * by timeouts — the component only needs to render based on state.
 *
 * onPlayerReady is intentionally excluded — it is a UI concern
 * owned by ArenaScreen. This hook only drives phase transitions.
 *
 * @param player1 - The local player (YOUR EGG, right side)
 * @param player2 - The opponent (ENEMY EGG, left side)
 * @param onGameEnd - Callback fired when the game concludes
 * @param readyCount - Number of players who have confirmed ready
 * @returns Current game state
 */
export function useGameState(
  player1: Player,
  player2: Player,
  onGameEnd: (result: GameResult) => void,
  readyCount: number,
  predefinedWinner?: string | null,
): { state: GameState } {
  const initialState = useMemo(
    () => createInitialState(player1.id, player2.id),
    // Initial state is created once on mount — player IDs must not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [state, dispatch] = useReducer(gameReducer, initialState);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onGameEndRef = useRef(onGameEnd);
  onGameEndRef.current = onGameEnd;

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Start countdown once the local player confirms ready
  useEffect(() => {
    if (readyCount >= 1 && state.phase === "waiting_ready") {
      dispatch({ type: "START_COUNTDOWN" });
    }
  }, [readyCount, state.phase]);

  // Drive phase transitions with timed dispatches
  useEffect(() => {
    clearTimer();
    const { timing } = GAME_CONFIG;

    switch (state.phase) {
      case "countdown": {
        if (state.countdownValue > 0) {
          timerRef.current = setTimeout(() => {
            dispatch({ type: "COUNTDOWN_TICK" });
          }, timing.countdownStepMs);
        } else {
          timerRef.current = setTimeout(() => {
            dispatch({ type: "START_FIGHTING" });
          }, timing.fightDisplayMs);
        }
        break;
      }

      case "fighting": {
        timerRef.current = setTimeout(() => {
          const winnerId = resolveRound(player1.id, player2.id, predefinedWinner);
          dispatch({ type: "IMPACT", winnerId });
        }, timing.fightDurationMs);
        break;
      }

      case "impact": {
        timerRef.current = setTimeout(() => {
          dispatch({ type: "SHOW_ROUND_RESULT" });
        }, timing.impactHoldMs);
        break;
      }

      case "round-result": {
        timerRef.current = setTimeout(() => {
          dispatch({ type: "ADVANCE" });
        }, timing.roundResultMs);
        break;
      }

      case "game-over": {
        if (state.gameWinnerId) {
          const loserId =
            state.gameWinnerId === player1.id ? player2.id : player1.id;
          onGameEndRef.current({
            winnerId: state.gameWinnerId,
            loserId,
            isTie: false,
            finalScores: state.wins,
            roundHistory: state.roundHistory,
          });
        } else {
          onGameEndRef.current({
            winnerId: null,
            loserId: null,
            isTie: true,
            finalScores: state.wins,
            roundHistory: state.roundHistory,
          });
        }
        break;
      }
    }

    return clearTimer;
  }, [state.phase, state.countdownValue, player1.id, player2.id]);

  return { state };
}
